import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAddresses, addAddress, updateAddress, deleteAddress } from '../lib/api';
import AccountSidebar from '../components/AccountSidebar';
import LocationPickerModal from '../components/LocationPickerModal';
import MapPreview from '../components/MapPreview';
import '../pages/OrdersPage.css';
import './AddressesPage.css';

const EMPTY_FORM = { label: 'Home', address: '', apartment: '', city: '', state: '', pincode: '', phone: '', isDefault: false, lat: null, lng: null, deliveryLocation: '' };

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function AddressForm({ initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [locationModalOpen, setLocationModalOpen] = useState(false);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const handleConfirmLocation = ({ address: locAddr, lat, lng }) => {
    setForm(f => ({ ...f, lat, lng, deliveryLocation: locAddr }));
    setLocationModalOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.address || !form.city || !form.state || !form.pincode || !form.phone) {
      setErr('Please fill in all required fields.'); return;
    }
    if (!/^\d{6}$/.test(form.pincode)) { setErr('Enter a valid 6-digit PIN code.'); return; }
    if (!/^\d{10}$/.test(form.phone))  { setErr('Enter a valid 10-digit phone number.'); return; }
    setSaving(true);
    try {
      await onSave(form);
    } catch (e2) {
      setErr(e2.message || 'Could not save address.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <form className="adr-form" onSubmit={submit}>
        {err && <div className="adr-alert">{err}</div>}

        <div className="adr-row">
          <label className="adr-label">
            Label
            <input className="adr-input" value={form.label} onChange={set('label')} placeholder="Home / Work" />
          </label>
          <label className="adr-label">
            Phone
            <input className="adr-input" value={form.phone} onChange={set('phone')} maxLength={10} placeholder="10-digit number" />
          </label>
        </div>

        <label className="adr-label">
          Address
          <input className="adr-input" value={form.address} onChange={set('address')} placeholder="House no., street" />
        </label>

        <label className="adr-label">
          Apartment, suite, etc. (optional)
          <input className="adr-input" value={form.apartment} onChange={set('apartment')} />
        </label>

        <div className="adr-row adr-row--3">
          <label className="adr-label">
            City
            <input className="adr-input" value={form.city} onChange={set('city')} />
          </label>
          <label className="adr-label">
            State
            <input className="adr-input" value={form.state} onChange={set('state')} />
          </label>
          <label className="adr-label">
            PIN code
            <input className="adr-input" value={form.pincode} onChange={set('pincode')} maxLength={6} />
          </label>
        </div>

        {/* Map pin picker */}
        <div className="adr-location-wrap">
          {form.lat && form.lng ? (
            <div className="adr-location-card">
              <MapPreview lat={form.lat} lng={form.lng} />
              <div className="adr-location-card__row">
                <span className="adr-location-card__icon"><PinIcon /></span>
                <p className="adr-location-card__text">{form.deliveryLocation || 'Location pinned'}</p>
                <button type="button" className="adr-link" onClick={() => setLocationModalOpen(true)}>Change</button>
              </div>
            </div>
          ) : (
            <button type="button" className="adr-location-btn" onClick={() => setLocationModalOpen(true)}>
              <PinIcon /> Pin exact location on map <span className="adr-location-btn__hint">(optional, helps faster delivery)</span>
            </button>
          )}
        </div>

        <label className="adr-check">
          <input type="checkbox" checked={!!form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
          Set as default address
        </label>

        <div className="adr-form__actions">
          <button type="button" className="adr-btn" onClick={onCancel}>Cancel</button>
          <button type="submit" className="adr-btn adr-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save address'}
          </button>
        </div>
      </form>

      <LocationPickerModal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        onConfirm={handleConfirmLocation}
        initialLocation={form.lat && form.lng ? { lat: form.lat, lng: form.lng, address: form.deliveryLocation } : null}
        formAddress={{ address: form.address, city: form.city, state: form.state, pincode: form.pincode }}
      />
    </>
  );
}

export default function AddressesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/account'); return; }
    fetchAddresses();
  }, [user]); // eslint-disable-line

  const fetchAddresses = async () => {
    setLoading(true); setFetchErr('');
    try {
      const data = await getAddresses();
      setAddresses(data || []);
    } catch (err) {
      setFetchErr(err.message || 'Failed to load addresses.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (form) => {
    const saved = await addAddress(form);
    setAddresses(prev => {
      const next = form.isDefault ? prev.map(a => ({ ...a, isDefault: false })) : prev;
      return [...next, saved];
    });
    setAdding(false);
  };

  const handleUpdate = async (id, form) => {
    const saved = await updateAddress(id, form);
    setAddresses(prev => prev.map(a => {
      if (a._id === id) return saved;
      return form.isDefault ? { ...a, isDefault: false } : a;
    }));
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await deleteAddress(id);
      setAddresses(prev => prev.filter(a => a._id !== id));
    } catch (err) {
      alert(err.message || 'Could not delete address.');
    }
  };

  if (!user) return null;

  return (
    <div className="ord-page">
      <AccountSidebar user={user} onLogout={onLogout} active="addresses" />

      <main className="ord-main adr-main">
        <div className="ord-header">
          <div>
            <h1 className="ord-title">Addresses</h1>
            <p className="ord-subtitle">
              {loading ? 'Loading…' : `${addresses.length} saved address${addresses.length === 1 ? '' : 'es'}`}
            </p>
          </div>
          {!adding && (
            <button className="ord-btn ord-btn--primary" onClick={() => { setAdding(true); setEditingId(null); }}>
              + Add new address
            </button>
          )}
        </div>

        {adding && (
          <section className="adr-card">
            <h2 className="adr-card__title">New address</h2>
            <AddressForm onCancel={() => setAdding(false)} onSave={handleAdd} />
          </section>
        )}

        {loading && (
          <div className="adr-skel-list">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="adr-card adr-skel-card">
                <div className="skel" style={{ width: '40%', height: 16, borderRadius: 6 }} />
                <div className="skel" style={{ width: '70%', height: 13, borderRadius: 5, marginTop: 12 }} />
                <div className="skel" style={{ width: '55%', height: 13, borderRadius: 5, marginTop: 8 }} />
                <div className="skel" style={{ width: '35%', height: 13, borderRadius: 5, marginTop: 8 }} />
                <div className="adr-skel-card__actions">
                  <div className="skel" style={{ width: 70, height: 32, borderRadius: 8 }} />
                  <div className="skel" style={{ width: 70, height: 32, borderRadius: 8 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && fetchErr && (
          <div className="ord-empty">
            <span className="ord-empty__emoji">⚠️</span>
            <h3>Couldn't load addresses</h3>
            <p>{fetchErr}</p>
            <button className="ord-btn ord-btn--primary" onClick={fetchAddresses} style={{ display: 'inline-flex', width: 'auto', padding: '0 28px', marginTop: '12px' }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !fetchErr && addresses.length === 0 && !adding && (
          <div className="ord-empty">
            <span className="ord-empty__emoji">📍</span>
            <h3>No addresses saved yet</h3>
            <p>Add a delivery address to speed up checkout next time.</p>
            <button className="ord-btn ord-btn--primary" style={{ display: 'inline-flex', width: 'auto', padding: '0 28px' }} onClick={() => setAdding(true)}>
              Add your first address
            </button>
          </div>
        )}

        {!loading && !fetchErr && addresses.length > 0 && (
          <div className="adr-list">
            {addresses.map(addr => (
              <div className="adr-card" key={addr._id}>
                {editingId === addr._id ? (
                  <>
                    <h2 className="adr-card__title">Edit address</h2>
                    <AddressForm
                      initial={addr}
                      onCancel={() => setEditingId(null)}
                      onSave={(form) => handleUpdate(addr._id, form)}
                    />
                  </>
                ) : (
                  <>
                    <div className="adr-card__hd">
                      <span className="adr-card__label"><PinIcon /> {addr.label}{addr.isDefault && <span className="adr-default">Default</span>}</span>
                      <div className="adr-card__actions">
                        <button className="adr-link" onClick={() => { setEditingId(addr._id); setAdding(false); }}>Edit</button>
                        <button className="adr-link adr-link--danger" onClick={() => handleDelete(addr._id)}>Delete</button>
                      </div>
                    </div>
                    {addr.lat && addr.lng && (
                      <div className="adr-map-preview">
                        <MapPreview lat={addr.lat} lng={addr.lng} />
                      </div>
                    )}
                    <p className="adr-card__text">
                      {addr.address}{addr.apartment ? `, ${addr.apartment}` : ''}<br />
                      {addr.city}, {addr.state} {addr.pincode}<br />
                      Phone: {addr.phone}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}